import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnInit,
    ViewEncapsulation,
    input,
    output,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ReportingFieldInfo } from "./data.ingestion.service";

export enum PredicateType {
    AND = "AND",
    OR = "OR",
    FIELD = "FIELD",
}

export interface PredicateNode {
    id?: string;
    op?:
        | "And"
        | "Or"
        | "Equals"
        | "NotEquals"
        | "GreaterThan"
        | "GreaterThanOrEqual"
        | "LessThan"
        | "LessThanOrEqual"
        | "In"
        | "NotIn"
        | "Like"
        | "NotLike";
    columnName?: string;
    value?: any;
    children?: PredicateNode[];
    // Frontend-only properties for UI
    type?: PredicateType;
    valueType?: string;
}

@TypeManagerDecorator("s25-ng-predicate-builder")
@Component({
    selector: "s25-ng-predicate-builder",
    template: `
        <div class="predicate-builder">
            <div class="predicate-tree">
                <s25-ng-predicate-node
                    [node]="predicate()"
                    [availableFields]="availableFields()"
                    [level]="0"
                    [isRoot]="true"
                    (nodeChanged)="onNodeChanged($event)"
                    (nodeRemoved)="onNodeRemoved($event)"
                >
                </s25-ng-predicate-node>
            </div>
        </div>
    `,
    styles: `
        .predicate-builder {
            padding: 15px;
        }

        .predicate-tree {
            min-height: 100px;
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 15px;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionPredicateBuilderComponent implements OnInit {
    availableFields = input<ReportingFieldInfo[]>([]);
    predicate = input<PredicateNode | null>(null);
    predicateChanged = output<PredicateNode | null>();

    // Internal predicate signal with default value
    private _predicate = signal<PredicateNode>({
        id: "root",
        op: "And",
        children: [],
    });

    constructor(
        private elem: ElementRef,
        private cdRef: ChangeDetectorRef,
        private z: NgZone,
    ) {}

    ngOnInit() {
        // Set default predicate if none provided
        if (!this.predicate()) {
            this.predicateChanged.emit(this._predicate());
        } else {
            // If predicate exists but is a FIELD type, wrap it in an AND root
            if (this.predicate()!.type === PredicateType.FIELD) {
                const wrappedPredicate = {
                    id: this.generateId(),
                    op: "And" as const,
                    children: [this.predicate()!],
                };
                this.predicateChanged.emit(wrappedPredicate);
            }
        }
    }

    onNodeChanged(node: PredicateNode) {
        this.predicateChanged.emit(node);
    }

    onNodeRemoved(nodeId: string) {
        // If the root node is removed or predicate is falsy, create a new default root node
        if (!this.predicate() || this.predicate()!.id === nodeId) {
            const newRootNode: PredicateNode = {
                id: this.generateId(),
                op: "And" as const,
                children: [],
            };
            this.predicateChanged.emit(newRootNode);
        } else {
            // Otherwise, emit the updated predicate
            this.predicateChanged.emit(this.predicate());
        }
    }

    private generateId(): string {
        return "node_" + Math.random().toString(36).substr(2, 9);
    }
}
